import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { createCryptoDepositController } from './crypto-deposit-controller.js';
import { prefetchCryptoDepositSource } from './crypto-deposit-source.js';
export function useCryptoDepositCapability(client, surface, t0) {
  const $ = _c(11);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== client.cacheKey || $[1] !== surface) {
    t1 = ["onramp", client.cacheKey, "crypto-deposit-capability", surface];
    $[0] = client.cacheKey;
    $[1] = surface;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== client.cryptoDeposit || $[4] !== surface) {
    t2 = t3 => {
      const {
        signal
      } = t3;
      return client.cryptoDeposit?.capability(surface, signal) ?? Promise.resolve(false);
    };
    $[3] = client.cryptoDeposit;
    $[4] = surface;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      persist: false
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== enabled || $[8] !== t1 || $[9] !== t2) {
    t4 = {
      queryKey: t1,
      enabled,
      queryFn: t2,
      retry: false,
      staleTime: 60000,
      gcTime: 0,
      meta: t3
    };
    $[7] = enabled;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return useQuery(t4);
}
export function useCryptoDepositSourcePrefetch(client, options) {
  const $ = _c(6);
  const {
    surface,
    locale,
    enabled
  } = options;
  let t0;
  let t1;
  if ($[0] !== client || $[1] !== enabled || $[2] !== locale || $[3] !== surface) {
    t0 = () => {
      if (!enabled) {
        return;
      }
      let stopped = false;
      const unregister = client.onDispose(() => {
        stopped = true;
      });
      prefetchCryptoDepositSource(client, surface, locale).catch(error => {
        if (stopped || error instanceof Error && error.name === "AbortError") {
          return;
        }
        client.observeFunnel?.({
          stage: "discovery",
          outcome: "error",
          errorCode: "provider_unavailable"
        });
      });
      return () => {
        stopped = true;
        unregister();
      };
    };
    t1 = [client, enabled, locale, surface];
    $[0] = client;
    $[1] = enabled;
    $[2] = locale;
    $[3] = surface;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
}
export function useCryptoDeposit(client, options) {
  const {
    surface,
    locale,
    active,
    operationId,
    createId,
    onComplete
  } = options;
  const lifecycle = useMemo(() => ({
    actions: createCryptoDepositController(client, surface, locale, createId, operationId ?? null),
    owners: 0
  }), [client, surface, locale, createId, operationId]);
  const actions = lifecycle.actions;
  const state = useSyncExternalStore(actions.subscribe, actions.getSnapshot, actions.getSnapshot);
  useEffect(() => {
    lifecycle.owners += 1;
    const unregister = client.onDispose(actions.dispose);
    return () => {
      lifecycle.owners -= 1;
      unregister();
      // React replays effects synchronously in Strict Mode. Defer irreversible
      // disposal so a replay can reclaim the same in-flight controller.
      queueMicrotask(() => {
        if (lifecycle.owners === 0) actions.dispose();
      });
    };
  }, [client, lifecycle, actions]);
  useEffect(() => {
    if (!onComplete) return undefined;
    return actions.onComplete(onComplete);
  }, [actions, onComplete]);
  useEffect(() => {
    if (!active || state.operation) return;
    if (operationId) void actions.resume(operationId);else void actions.start();
  }, [actions, active, operationId, state.operation]);
  useEffect(() => {
    const operation = state.operation;
    if (!active || !operation?.instructions || ['complete', 'route_failed', 'refund'].includes(operation.state)) return;
    const timer = setInterval(() => void actions.refresh(), 7_000);
    return () => clearInterval(timer);
  }, [actions, active, state.operation]);
  return {
    state,
    actions
  };
}